import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ListActionBar from 'src/components/ListActionBar';
import StatusButton from 'src/components/StatusButton';
import StatusChip from 'src/components/StatusChip';
import ColumnVisibilityMenu from 'src/components/ColumnVisibilityMenu';
import TableActionButton from 'src/components/TableActionButton';
import DeviceTagChip from 'src/components/DeviceTagChip';
import TableContainer from 'src/components/Table/TableContainer';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableHeadTypography from 'src/components/TableHeadTypography';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useColumnVisibility } from 'src/hooks/columnVisibility';
import Iconify from 'src/components/Iconify';
import Image from 'src/components/Image';
import DeviceStatusModal from './DeviceStatusModal';
import DeviceBulkStatusModal from './DeviceBulkStatusModal';
import BulkUpdateModal from './bulkUpdate/BulkUpdateModal';
import useDialog from 'src/hooks/useDialog';
import { useCanBulkUpdateDevices } from 'src/utils/featureFlags';
import { formatDate } from 'src/utils/formatTime';
import { DEVICES_PREVIOUS_PATH } from './deviceDetails/constant';
import { useTagOptions } from 'src/hooks/tag';
import {
  useDeviceLogCollectorVersions,
  useDeviceOsVersions,
  useExportDevices,
  useInfiniteDevices,
} from 'src/hooks/device';
import { DEVICE_STATUS, getDeviceStatusLabel } from 'src/utils/enums/deviceStatus';
import {
  HEALTH_STATUS,
  getHealthStatusColor,
  getHealthStatusLabel,
} from 'src/utils/enums/healthStatus';
import { WDAC_STATUS, getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { parseQueryArray, parseQueryValue } from 'src/utils/uri';
import HighlightedText from 'src/components/HighlightedText';

const PAGE_LIMIT = 50;
const DEFAULT_SORT = { column: 'lastContactDate', direction: 'desc' };
const DEFAULT_FILTERS = {
  healthStatuses: [],
  wdacStatuses: [],
  statuses: [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown],
  tagId: '',
  osVersion: '',
  logCollectorVersion: '',
};
const DEFAULT_VISIBLE_COLUMNS = [
  'status',
  'name',
  'healthStatus',
  'lastContactDate',
  'wdacDeviceStatus',
  'osVersion',
  'logCollectorVersion',
  'tagName',
];

const hasInvalidHealthyOfflinePair = healthStatuses =>
  healthStatuses?.includes(HEALTH_STATUS.Healthy) &&
  healthStatuses?.includes(HEALTH_STATUS.Offline) &&
  !healthStatuses?.includes(HEALTH_STATUS.ConfigIssue);

const normalizeHealthStatuses = (nextHealthStatuses, previousHealthStatuses = null) => {
  const distinctStatuses = [...new Set(nextHealthStatuses || [])];

  if (!hasInvalidHealthyOfflinePair(distinctStatuses)) {
    return distinctStatuses;
  }

  const previousDistinctStatuses = [...new Set(previousHealthStatuses || [])];
  const hadAllThreeSelected =
    previousDistinctStatuses.length === 3 &&
    previousDistinctStatuses.includes(HEALTH_STATUS.Healthy) &&
    previousDistinctStatuses.includes(HEALTH_STATUS.ConfigIssue) &&
    previousDistinctStatuses.includes(HEALTH_STATUS.Offline);

  if (
    hadAllThreeSelected &&
    distinctStatuses.length === 2 &&
    distinctStatuses.includes(HEALTH_STATUS.Healthy) &&
    distinctStatuses.includes(HEALTH_STATUS.Offline)
  ) {
    return [];
  }

  if (previousHealthStatuses) {
    const addedStatuses = distinctStatuses.filter(
      status => !previousHealthStatuses.includes(status),
    );

    if (addedStatuses.includes(HEALTH_STATUS.Healthy)) {
      return distinctStatuses.filter(status => status !== HEALTH_STATUS.Healthy);
    }

    if (addedStatuses.includes(HEALTH_STATUS.Offline)) {
      return distinctStatuses.filter(status => status !== HEALTH_STATUS.Offline);
    }

    return previousHealthStatuses;
  }

  return distinctStatuses.filter(status => status !== HEALTH_STATUS.Offline);
};

const DEVICES_TABLE_COLUMNS = [
  { key: 'status', label: 'Status', required: true, width: '136px' },
  { key: 'name', label: 'Device', sortable: true, sortKey: 'name', required: true, width: '210px' },
  { key: 'healthStatus', label: 'Health Status', width: '140px' },
  {
    key: 'lastContactDate',
    label: 'Last Contact',
    sortable: true,
    sortKey: 'lastContactDate',
    width: '160px',
  },
  {
    key: 'lastWdacHeartbeatDate',
    label: 'Last Heartbeat',
    sortable: true,
    sortKey: 'lastWdacHeartbeatDate',
    width: '160px',
  },
  { key: 'wdacDeviceStatus', label: 'WDAC Status', width: '120px' },
  { key: 'osVersion', label: 'OS Version', width: '160px' },
  {
    key: 'logCollectorVersion',
    label: 'Log Collector',
    sortable: true,
    sortKey: 'logCollectorVersion',
    width: '160px',
  },
  { key: 'note', label: 'Note', width: '56px' },
  { key: 'tagName', label: 'Device Tags', width: '220px' },
];

const getInitialFilters = search => {
  const params = new URLSearchParams(search);
  const healthStatuses = parseQueryArray(params, ['HealthStatuses', 'HealthStatus']);
  const wdacStatuses = parseQueryArray(params, ['WdacStatuses']);
  const statuses = parseQueryArray(params, ['Statuses']);
  const tagId = parseQueryValue(params, ['TagId']);
  const osVersion = parseQueryValue(params, ['OsVersion']);
  const logCollectorVersion = parseQueryValue(params, ['LogCollectorVersion']);

  return {
    healthStatuses: normalizeHealthStatuses(
      healthStatuses.length ? healthStatuses : DEFAULT_FILTERS.healthStatuses,
    ),
    wdacStatuses,
    statuses: statuses.length ? statuses : DEFAULT_FILTERS.statuses,
    tagId: tagId || '',
    osVersion: osVersion || '',
    logCollectorVersion: logCollectorVersion || '',
  };
};

const DevicesColumnHeader = ({ label, sortable, sortActive, sortDirection, onSort }) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();

  return (
    <Stack
      direction="row"
      gap={0.5}
      alignItems="center"
      onClick={sortable ? onSort : undefined}
      sx={{ cursor: sortable ? 'pointer' : 'default' }}>
      <TableHeadTypography
        hasSorting={sortActive}
        mediumScreen={mediumScreen}
        largeScreen={largeScreen}
        smallScreen={smallScreen}>
        {label}
      </TableHeadTypography>
      {sortable ? (
        <Iconify
          icon={
            sortActive
              ? sortDirection === 'asc'
                ? 'mdi:arrow-up'
                : 'mdi:arrow-down'
              : 'mdi:swap-vertical'
          }
          width={14}
          height={14}
          color={sortActive ? '#7DFF92' : '#9CA3AF'}
        />
      ) : null}
    </Stack>
  );
};

const DevicesTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  // Dashboard links arrive with the legacy capitalised params, so they set the starting point; the
  // action bar's own params win over them when the page is being returned to.
  const initialFilters = useMemo(() => getInitialFilters(location.search), [location.search]);

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(() => readFilterParams(initialFilters) ?? initialFilters);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const { visibleColumnDefinitions, isColumnVisible, setColumnVisible, resetColumns } =
    useColumnVisibility('devices', DEVICES_TABLE_COLUMNS, DEFAULT_VISIBLE_COLUMNS);

  useFilterParams(filters);
  const trimmedSearchValue = useMemo(() => searchValue.trim(), [searchValue]);

  const apiQuery = useMemo(() => {
    return {
      ...(trimmedSearchValue ? { Search: trimmedSearchValue } : {}),
      ...(filters.healthStatuses.length ? { HealthStatuses: filters.healthStatuses } : {}),
      ...(filters.wdacStatuses.length ? { WdacStatuses: filters.wdacStatuses } : {}),
      ...(filters.statuses.length ? { Statuses: filters.statuses } : {}),
      ...(filters.tagId ? { TagIds: [Number(filters.tagId)] } : {}),
      ...(filters.osVersion ? { OsVersion: filters.osVersion } : {}),
      ...(filters.logCollectorVersion ? { LogCollectorVersion: filters.logCollectorVersion } : {}),
      SortColumn: sort.column,
      SortDesc: sort.direction === 'desc',
    };
  }, [filters, sort, trimmedSearchValue]);

  const { data: tagOptionsData } = useTagOptions({ search: '' });
  const { data: osVersionData } = useDeviceOsVersions();
  const { data: logCollectorVersionData } = useDeviceLogCollectorVersions();

  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);
  const osVersionOptions = useMemo(() => osVersionData?.properties || [], [osVersionData]);
  const logCollectorOptions = useMemo(
    () => (logCollectorVersionData?.versions || []).filter(Boolean),
    [logCollectorVersionData],
  );

  const devicesQuery = useInfiniteDevices({
    apiQuery,
    limit: PAGE_LIMIT,
  });

  const exportDevicesMutation = useExportDevices();
  const canBulkUpdateDevices = useCanBulkUpdateDevices();
  const bulkUpdateDialog = useDialog();

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.devices || []) || [],
    [devicesQuery.data],
  );
  const totalCount = devicesQuery.data?.pages?.[0]?.count ?? 0;

  const loadMore = useCallback(() => {
    if (devicesQuery.hasNextPage) {
      devicesQuery.fetchNextPage();
    }
  }, [devicesQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: devicesQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const [selectedRows, setSelectedRows] = useState(() => new Set());
  useEffect(() => {
    setSelectedRows(new Set());
  }, [apiQuery]);

  const toggleRowSelection = useCallback(deviceId => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  }, []);

  const [isSingleStatusDialogOpen, setIsSingleStatusDialogOpen] = useState(false);
  const [isBulkStatusDialogOpen, setIsBulkStatusDialogOpen] = useState(false);
  const [activeDevice, setActiveDevice] = useState(null);

  const handleOpenSingleStatusDialog = useCallback(device => {
    setActiveDevice(device);
    setIsSingleStatusDialogOpen(true);
  }, []);

  const handleOpenBulkStatusDialog = useCallback(() => {
    setIsBulkStatusDialogOpen(true);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    setIsSingleStatusDialogOpen(false);
    setIsBulkStatusDialogOpen(false);
    setActiveDevice(null);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      const data = await exportDevicesMutation.mutateAsync({ query: apiQuery });
      if (!data?.fileContents) {
        enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
        return;
      }

      const fileData = atob(data.fileContents);
      const byteArray = new Uint8Array(fileData.length);
      for (let index = 0; index < fileData.length; index += 1) {
        byteArray[index] = fileData.charCodeAt(index);
      }

      const blob = new Blob([byteArray], { type: data.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.fileDownloadName || 'devices-export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error) {
      enqueueSnackbar('Something went wrong.', { variant: 'error', autoHideDuration: 3000 });
    }
  }, [apiQuery, enqueueSnackbar, exportDevicesMutation]);

  const handleRowClick = useCallback(
    deviceId => {
      const returnPath = `${window.location.pathname}${window.location.search}`;
      sessionStorage.setItem(DEVICES_PREVIOUS_PATH, returnPath);
      navigate(`${PORTAL_ROUTE}/devices/${deviceId}/general`);
    },
    [navigate],
  );

  const handleSort = useCallback(column => {
    if (!column.sortable || !column.sortKey) {
      return;
    }

    setSort(prev =>
      prev.column === column.sortKey
        ? { column: column.sortKey, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { column: column.sortKey, direction: 'desc' },
    );
  }, []);

  const filterOptions = useMemo(
    () => [
      {
        key: 'healthStatuses',
        type: 'checkbox',
        label: 'Health Status',
        options: [
          {
            value: HEALTH_STATUS.Healthy,
            label: 'Healthy',
            disabled: currentFilters =>
              hasInvalidHealthyOfflinePair([
                ...(currentFilters.healthStatuses || []),
                HEALTH_STATUS.Healthy,
              ]),
          },
          {
            value: HEALTH_STATUS.ConfigIssue,
            label: 'Config Issue',
          },
          {
            value: HEALTH_STATUS.Offline,
            label: 'Offline',
            disabled: currentFilters =>
              hasInvalidHealthyOfflinePair([
                ...(currentFilters.healthStatuses || []),
                HEALTH_STATUS.Offline,
              ]),
          },
        ],
      },
      {
        key: 'wdacStatuses',
        type: 'checkbox',
        label: 'WDAC Status',
        options: [
          { value: WDAC_STATUS.Unknown, label: 'Unknown' },
          { value: WDAC_STATUS.Enforced, label: 'Enforced' },
          { value: WDAC_STATUS.Audit, label: 'Audit' },
        ],
      },
      {
        key: 'statuses',
        type: 'checkbox',
        label: 'Device Status',
        options: [
          { value: DEVICE_STATUS.Approved, label: 'Approved' },
          { value: DEVICE_STATUS.Revoked, label: 'Revoked' },
          { value: DEVICE_STATUS.Archived, label: 'Archived' },
          { value: DEVICE_STATUS.Unknown, label: 'No Status' },
        ],
      },
      {
        key: 'tagId',
        type: 'select',
        label: 'Device Tag',
        options: [
          { value: '', label: 'All Tags' },
          ...tagOptions.map(tag => ({ value: String(tag.id), label: tag.name })),
        ],
      },
      {
        key: 'osVersion',
        type: 'select',
        label: 'OS Version',
        options: [
          { value: '', label: 'All OS versions' },
          ...osVersionOptions.map(value => ({ value, label: value })),
        ],
      },
      {
        key: 'logCollectorVersion',
        type: 'select',
        label: 'Log Collector',
        options: [
          { value: '', label: 'All versions' },
          ...logCollectorOptions.map(value => ({ value, label: value })),
        ],
      },
    ],
    [logCollectorOptions, osVersionOptions, tagOptions],
  );

  const renderCell = useCallback(
    (column, row) => {
      switch (column.key) {
        case 'status': {
          const isSelected = selectedRows.has(row.id);

          return (
            <TableCell
              key={`${row.id}-status`}
              onClick={event => event.stopPropagation()}
              sx={{ py: 0.5, px: 1, width: column.width }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={() => toggleRowSelection(row.id)}
                  sx={{ p: 0.5, '& .MuiSvgIcon-root path': { fill: '#7DFF92' } }}
                />
                <StatusButton
                  status={row.status}
                  size="small"
                  sx={{ fontSize: '11px', minWidth: 76, padding: '2px 8px' }}
                  onClick={event => {
                    event.stopPropagation();
                    handleOpenSingleStatusDialog(row);
                  }}>
                  {getDeviceStatusLabel(row.status)}
                </StatusButton>
              </Stack>
            </TableCell>
          );
        }
        case 'note':
          return (
            <TableCell
              key={`${row.id}-note`}
              onClick={event => event.stopPropagation()}
              align="center"
              sx={{ width: column.width }}>
              {row.notes ? (
                <Tooltip
                  title={row.notes}
                  arrow
                  componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F' } } }}>
                  <Box
                    width={20}
                    height={20}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center">
                    <Image
                      src="/static/icons/notes-notepad.svg"
                      alt="Notes"
                      width={18}
                      height={18}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  --
                </Typography>
              )}
            </TableCell>
          );
        case 'name':
          return (
            <TableCell key={`${row.id}-name`} sx={{ width: column.width }}>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  fontWeight: 400,
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                  '& .highlight': {
                    backgroundColor: '#7DFF92',
                    color: '#000',
                    fontWeight: 700,
                    padding: '0 2px',
                    borderRadius: '2px',
                  },
                }}>
                <HighlightedText text={row.name} searchValue={trimmedSearchValue} fallback="--" />
              </Typography>
            </TableCell>
          );
        case 'tagName':
          return (
            <TableCell key={`${row.id}-tags`} sx={{ width: column.width }}>
              <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                {(row.tags || []).length ? (
                  (row.tags || []).map(tag => <DeviceTagChip key={tag.id} label={tag.name} />)
                ) : (
                  <Typography variant="caption">No tags</Typography>
                )}
              </Stack>
            </TableCell>
          );
        case 'healthStatus': {
          const color = getHealthStatusColor(row.healthStatus);

          return (
            <TableCell key={`${row.id}-health`} sx={{ width: column.width }}>
              <StatusChip
                label={getHealthStatusLabel(row.healthStatus)}
                color={color}
                backgroundColor={`${color}1F`}
              />
            </TableCell>
          );
        }
        case 'lastContactDate':
        case 'lastWdacHeartbeatDate':
          return (
            <TableCell key={`${row.id}-${column.key}`} sx={{ width: column.width }}>
              <Typography variant="caption">{formatDate(row[column.key]) || '--'}</Typography>
            </TableCell>
          );
        case 'wdacDeviceStatus':
          return (
            <TableCell key={`${row.id}-wdac`} sx={{ width: column.width }}>
              <Typography variant="caption">{getWdacStatusLabel(row.wdacDeviceStatus)}</Typography>
            </TableCell>
          );
        default:
          return (
            <TableCell key={`${row.id}-${column.key}`} sx={{ width: column.width }}>
              <Typography variant="caption">{row[column.key] || '--'}</Typography>
            </TableCell>
          );
      }
    },
    [handleOpenSingleStatusDialog, selectedRows, toggleRowSelection, trimmedSearchValue],
  );

  const handleRefresh = useCallback(async () => {
    await devicesQuery.refetch();
    enqueueSnackbar('Device list successfully refreshed.', {
      variant: 'success',
      autoHideDuration: 3000,
    });
  }, [devicesQuery, enqueueSnackbar]);

  const handleFilterChange = useCallback(nextFilters => {
    setFilters(currentFilters => ({
      ...nextFilters,
      healthStatuses: normalizeHealthStatuses(
        nextFilters.healthStatuses ?? currentFilters.healthStatuses,
        currentFilters.healthStatuses || [],
      ),
    }));
  }, []);

  return (
    <Box sx={{ position: 'relative', mt: 3 }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Search devices"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={filterOptions}
        filterChips
        limitChips={2}
        filterButtonCount={totalCount}
        actionArea={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Tooltip title="Refresh">
              <span>
                <TableActionButton
                  variant="outlined"
                  onClick={handleRefresh}
                  disabled={devicesQuery.isRefetching}
                  sx={{
                    minWidth: 32,
                    width: 32,
                    px: 0.75,
                  }}>
                  <Iconify icon="material-symbols:refresh" width={18} height={18} />
                </TableActionButton>
              </span>
            </Tooltip>
            <ColumnVisibilityMenu
              columns={DEVICES_TABLE_COLUMNS}
              isVisible={isColumnVisible}
              onChange={setColumnVisible}
              onReset={resetColumns}
            />
            {selectedRows.size > 0 ? (
              <TableActionButton
                variant="outlined"
                onClick={handleOpenBulkStatusDialog}
                sx={{ px: 1.5 }}>
                Update Status
              </TableActionButton>
            ) : (
              <TableActionButton
                variant="outlined"
                onClick={handleExport}
                disabled={exportDevicesMutation.isLoading}
                sx={{ px: 1.5 }}>
                {exportDevicesMutation.isLoading ? 'Exporting...' : 'Export All'}
              </TableActionButton>
            )}
            {canBulkUpdateDevices && (
              <TableActionButton
                variant="outlined"
                onClick={bulkUpdateDialog.onOpen}
                sx={{ px: 1.5 }}>
                Bulk Update
              </TableActionButton>
            )}
          </Stack>
        }
      />

      <TableContainer
        roundedTop
        styles={{
          maxHeight: 'calc(100vh - 310px)',
        }}>
        <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {visibleColumnDefinitions.map(column => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead sx={{ backgroundColor: '#3F3F3F' }}>
            <TableRow>
              {visibleColumnDefinitions.map(column => (
                <TableCell
                  key={column.key}
                  sx={
                    column.key === 'status' ? { py: 0.5, px: 1, width: column.width } : undefined
                  }>
                  {column.key === 'status' ? (
                    <DevicesColumnHeader label={column.label} />
                  ) : (
                    <DevicesColumnHeader
                      label={column.label}
                      sortable={column.sortable}
                      sortActive={sort.column === column.sortKey}
                      sortDirection={sort.direction}
                      onSort={() => handleSort(column)}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
            {devicesQuery.isLoading ? (
              <LoadingRows columnCount={visibleColumnDefinitions.length} rowCount={6} />
            ) : (
              <>
                {devices.map((row, index) => (
                  <TableRow
                    key={row.id}
                    ref={index === devices.length - 1 ? sentryRef : null}
                    hover
                    onClick={() => handleRowClick(row.id)}
                    sx={{ cursor: 'pointer' }}>
                    {visibleColumnDefinitions.map(column => renderCell(column, row))}
                  </TableRow>
                ))}
                {devicesQuery.isFetchingNextPage ? (
                  <LoadingRows columnCount={visibleColumnDefinitions.length} rowCount={3} />
                ) : null}
                {!devicesQuery.isLoading && devices.length === 0 ? (
                  <NoDataCell colSpan={visibleColumnDefinitions.length}>
                    No devices found.
                  </NoDataCell>
                ) : null}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <DeviceStatusModal
        open={isSingleStatusDialogOpen}
        title="Update Device Status"
        description={activeDevice?.name || 'Update the selected device.'}
        deviceId={activeDevice?.id}
        initialStatus={activeDevice?.status || DEVICE_STATUS.Unknown}
        initialTags={activeDevice?.tags || []}
        onClose={handleCloseStatusDialog}
        onSaved={() => {
          setSelectedRows(new Set());
        }}
      />

      {canBulkUpdateDevices && (
        <BulkUpdateModal
          open={bulkUpdateDialog.open}
          query={apiQuery}
          onClose={bulkUpdateDialog.onClose}
        />
      )}
      <DeviceBulkStatusModal
        open={isBulkStatusDialogOpen}
        selectedCount={selectedRows.size}
        deviceIds={Array.from(selectedRows)}
        onClose={handleCloseStatusDialog}
        onSaved={() => {
          setSelectedRows(new Set());
        }}
      />
    </Box>
  );
};

export default DevicesTable;
